// Bootstrap 3 -> 5 data-attribute bridge.
//
// BS5 renamed every data-API attribute (data-toggle -> data-bs-toggle etc.), so
// unmigrated views keep working by mirroring the old attributes onto the new
// names — on page load and, via MutationObserver, on any injected markup
// (ajax_dialog, dialogs.js, Vue, Turbo...). Old attributes are left in place:
// nothing in BS5 reads them, and legacy code may still query them.
//
// It also papers over two BS3 markup conventions BS5's JS no longer handles:
// tabs/pills keep `.active` on the <li>, and initial active state must be
// mirrored onto the <a> for BS5's Tab to (de)activate panes correctly.

const ATTRIBUTE_MAP = {
  'data-toggle': 'data-bs-toggle',
  'data-target': 'data-bs-target',
  'data-dismiss': 'data-bs-dismiss',
  'data-parent': 'data-bs-parent',
  'data-ride': 'data-bs-ride',
  'data-slide': 'data-bs-slide',
  'data-slide-to': 'data-bs-slide-to',
  'data-placement': 'data-bs-placement',
  'data-container': 'data-bs-container',
  'data-content': 'data-bs-content',
  'data-html': 'data-bs-html',
  'data-trigger': 'data-bs-trigger',
  'data-delay': 'data-bs-delay',
  'data-backdrop': 'data-bs-backdrop',
  'data-keyboard': 'data-bs-keyboard',
  'data-interval': 'data-bs-interval',
  'data-offset': 'data-bs-offset',
  'data-animation': 'data-bs-animation',
  // BS3 moved the title attr here; BS5 reads data-bs-original-title
  'data-original-title': 'data-bs-original-title',
};
const OLD_ATTRIBUTES = Object.keys(ATTRIBUTE_MAP);
const SELECTOR = OLD_ATTRIBUTES.map((attr) => `[${attr}]`).join(',');

// Exported so jQuery plugin shims can mirror synchronously at instantiation
// time: the MutationObserver below is async, and a component created in the
// same tick its element was injected (Vue directives) would otherwise read
// its data-bs-* config before the mirror ran (e.g. data-placement ignored).
export function upgradeElement(element) {
  for (const oldAttr of OLD_ATTRIBUTES) {
    if (!element.hasAttribute(oldAttr)) {continue;}

    const value = element.getAttribute(oldAttr);

    // Legacy code updates data-* attributes at runtime (e.g. swapping a
    // tooltip's data-original-title after an async load), so always re-mirror.
    if (element.getAttribute(ATTRIBUTE_MAP[oldAttr]) !== value) {
      element.setAttribute(ATTRIBUTE_MAP[oldAttr], value);
    }
  }

  // BS3 tab markup: mirror the li's .active onto the toggle itself so BS5's
  // Tab JS can find the currently active tab when switching.
  const toggle = element.getAttribute('data-bs-toggle');

  if ((toggle === 'tab' || toggle === 'pill') && element.closest('li.active')) {
    element.classList.add('active');
  }
}

function upgradeTree(root) {
  if (root.matches?.(SELECTOR)) {upgradeElement(root);}
  root.querySelectorAll?.(SELECTOR).forEach(upgradeElement);
}

export function installDataAttributeBridge() {
  upgradeTree(document.documentElement);

  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'attributes') {
        upgradeElement(mutation.target);
        continue;
      }

      for (const node of mutation.addedNodes) {
        if (node.nodeType === Node.ELEMENT_NODE) {upgradeTree(node);}
      }
    }
  }).observe(document.documentElement, {
    childList: true,
    subtree: true,
    // Re-mirror when legacy code rewrites a data-* attribute in place. The
    // data-bs-* names are not in the filter, so mirroring cannot loop.
    attributes: true,
    attributeFilter: OLD_ATTRIBUTES,
  });

  // BS3 flagged an open dropdown with `.open` on the PARENT (.dropdown /
  // .btn-group); BS5 puts `.show` on the toggle and menu only. App CSS keys on
  // the parent (`.dropdown.open > .btn-default.dropdown-toggle` paints the
  // pressed toggle), so mirror it.
  const dropdownParent = (toggle) => toggle.closest('.dropdown, .dropup, .btn-group') || toggle.parentElement;

  // shown, not show: show.bs.dropdown is cancelable and a preventDefault() leaves
  // the dropdown closed (no .show), so .open would be stuck on the parent.
  document.addEventListener('shown.bs.dropdown', (event) => {
    dropdownParent(event.target)?.classList.add('open');
  });
  document.addEventListener('hidden.bs.dropdown', (event) => {
    dropdownParent(event.target)?.classList.remove('open');
  });

  // Keep BS3's li.active convention in sync when BS5's Tab JS switches tabs.
  document.addEventListener('shown.bs.tab', (event) => {
    event.target.closest('li')?.classList.add('active');
    if (event.relatedTarget) {
      event.relatedTarget.closest('li')?.classList.remove('active');
      event.relatedTarget.classList.remove('active');
    }
  });
}
